import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, animate } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import ZiggsLogo from "@/components/Logo";
import { useLenis } from "@/components/LenisProvider";

const EASE = [0.16, 1, 0.3, 1];
const LINES = [{ text: "SUA IDEIA," }, { text: "FORJADA EM" }, { text: "3D.", accent: true }];
const MATERIALS = ["PLA", "PETG", "TPU", "RESINA"];

export const HeroSection = () => {
    const [progress, setProgress] = useState(0);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 50, damping: 16 });
    const sy = useSpring(my, { stiffness: 50, damping: 16 });
    const { scrollTo } = useLenis();

    useEffect(() => {
        const controls = animate(0, 100, {
            duration: 2.4,
            delay: 0.5,
            ease: "linear",
            onUpdate: (v) => setProgress(Math.round(v)),
        });
        return () => controls.stop();
    }, []);

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set(((e.clientX - r.left) / r.width - 0.5) * 22);
        my.set(((e.clientY - r.top) / r.height - 0.5) * 22);
    };

    return (
        <section
            onMouseMove={onMouseMove}
            className="relative overflow-hidden pb-16 pt-28 md:pb-24 md:pt-40"
            data-testid="hero-section"
        >
            <div className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-forge-yellow/10 blur-[140px]" />

            <div className="zf-container grid items-center gap-14 lg:grid-cols-[1.15fr_1fr]">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="zf-eyebrow"
                    >
                        Estúdio de impressão 3D
                    </motion.p>

                    <h1 className="mt-6 font-display text-[clamp(2.5rem,7vw,5.5rem)] font-black uppercase leading-[0.95] tracking-tight text-forge-paper">
                        {LINES.map((l, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${l.accent ? "text-forge-yellow" : ""}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.08 + i * 0.13, ease: EASE }}
                                >
                                    {l.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-forge-dim md:text-lg"
                    >
                        Transformamos arquivos digitais em peças reais — protótipos, colecionáveis, presentes e
                        soluções sob medida, impressas camada por camada com acabamento de verdade.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/catalogo"
                            data-testid="hero-catalog-button"
                            className="group inline-flex items-center gap-3 bg-forge-yellow px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5 md:text-sm"
                        >
                            Explorar catálogo
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <button
                            onClick={() => scrollTo("#como-pedir")}
                            data-testid="hero-how-button"
                            className="inline-flex items-center gap-3 border border-forge-line px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-forge-paper transition-colors duration-300 hover:border-forge-yellow hover:text-forge-yellow md:text-sm"
                        >
                            Como funciona
                            <ArrowDown className="h-4 w-4" />
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="mt-10 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-forge-dim"
                    >
                        {MATERIALS.map((m) => (
                            <span key={m} className="border border-forge-line px-3 py-1.5">
                                {m}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    style={{ x: sx, y: sy }}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                    className="relative mx-auto w-full max-w-md lg:max-w-none"
                    data-testid="hero-print-zone"
                >
                    <div className="relative aspect-square border border-forge-line bg-black">
                        <div className="layer-lines absolute inset-0 opacity-70" aria-hidden />
                        <span className="absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-forge-yellow/70" aria-hidden />
                        <span className="absolute right-3 top-3 h-4 w-4 border-r-2 border-t-2 border-forge-yellow/70" aria-hidden />
                        <span className="absolute bottom-3 left-3 h-4 w-4 border-b-2 border-l-2 border-forge-yellow/70" aria-hidden />
                        <span className="absolute bottom-3 right-3 h-4 w-4 border-b-2 border-r-2 border-forge-yellow/70" aria-hidden />

                        <div className="absolute left-5 top-5 z-10 font-mono text-[10px] uppercase tracking-[0.25em] text-forge-dim">
                            Ziggs-Forge · FDM-01
                        </div>
                        <div
                            className="absolute right-5 top-5 z-10 font-mono text-[10px] uppercase tracking-[0.25em] text-forge-yellow"
                            data-testid="hero-print-progress"
                        >
                            {progress}%
                        </div>

                        <div className="print-scene absolute inset-0 flex items-center justify-center">
                            <div className="zf-logo print-target w-[64%]">
                                <ZiggsLogo className="h-auto w-full" />
                            </div>
                        </div>
                        <div className="zf-nozzle" aria-hidden />

                        <div className="absolute inset-x-0 bottom-0 z-10">
                            <div className="flex items-center justify-between px-5 pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-forge-dim">
                                <span>Imprimindo: ziggs-bot.gcode</span>
                                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-forge-yellow" />
                            </div>
                            <div className="h-0.5 w-full bg-white/10">
                                <div
                                    className="h-full bg-forge-yellow"
                                    style={{ width: `${progress}%`, transition: "width 120ms linear" }}
                                    data-testid="hero-print-bar"
                                />
                            </div>
                        </div>
                    </div>
                    <p className="mt-3 text-right font-mono text-[10px] uppercase tracking-[0.25em] text-forge-dim">
                        Passe o mouse no logotipo — a antena acende
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default HeroSection;
